/**
 * fbOAuth.js — Facebook OAuth2 client utility for Restreamer UI.
 *
 * Flow priority:
 *   1. Auth Code flow  (response_type=code) → server/index.js intercambia:
 *        code → short-lived token (2h) → long-lived token (60 días)
 *      Requiere App Secret configurado en Settings → Integrations.
 *
 *   2. Implicit flow  (response_type=token) → token 2h desde browser.
 *      Si el backend tiene App Secret configurado, lo upgradea a long-lived (60 días).
 *      Fallback cuando App Secret NO está configurado.
 *
 * Persistencia:
 *   - Cuentas guardadas en server/data/config.json (vía REST API /fb/*)
 *   - UI cache en localStorage para render inmediato sin round-trip.
 *
 * Estructura de cuenta en config.json:
 *   {
 *     fb_user_id:    string,        // PK
 *     name:          string,
 *     token_type:    'USER'|'PAGE',
 *     access_token:  string,        // AES-256-GCM encrypted (server-side)
 *     expires_at:    number,        // Unix ms
 *     scope_granted: string[],
 *     pages:         Page[],        // long-lived page tokens (server-side)
 *     updated_at:    number,
 *   }
 */

const LS_KEY = '@@restreamer-ui@@fb_oauth_v2';

// In development: /fb-server is proxied by setupProxy.js → http://localhost:3002
// In production:  reads window.__RESTREAMER_CONFIG__.FB_SERVER_URL (set in public/config.js)
//                 If empty, falls back to relative /fb-server (Caddy reverse proxy)
const _rtCfg = window.__RESTREAMER_CONFIG__ || {};
const SERVER_BASE = _rtCfg.FB_SERVER_URL
	? _rtCfg.FB_SERVER_URL.replace(/\/$/, '')
	: '/fb-server';

// ── localStorage cache (UI-only, no tokens stored here) ──────────────────────
function _loadCache() {
	try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
}
function _saveCache(data) {
	try { localStorage.setItem(LS_KEY, JSON.stringify(data)); } catch (_) { /* quota */ }
}

// ── Server API helpers ────────────────────────────────────────────────────────

async function _serverGet(path) {
	const res = await fetch(SERVER_BASE + path, { headers: { 'Content-Type': 'application/json' } });
	return res.json();
}

async function _serverPost(path, body) {
	const res = await fetch(SERVER_BASE + path, {
		method:  'POST',
		headers: { 'Content-Type': 'application/json' },
		body:    JSON.stringify(body),
	});
	return res.json();
}

async function _serverPut(path, body) {
	const res = await fetch(SERVER_BASE + path, {
		method:  'PUT',
		headers: { 'Content-Type': 'application/json' },
		body:    JSON.stringify(body),
	});
	return res.json();
}

async function _serverDelete(path) {
	const res = await fetch(SERVER_BASE + path, { method: 'DELETE' });
	return res.json();
}

// ── Config (App ID + App Secret presence) ────────────────────────────────────

/**
 * Get app_id and whether app_secret is configured from server.
 * Falls back to localStorage cache.
 * @returns {Promise<{ app_id: string, has_secret: boolean }>}
 */
const getConfig = async () => {
	try {
		const data = await _serverGet('/fb/config');
		const c = _loadCache();
		c.__fb_config = { app_id: data.app_id || '', has_secret: !!data.has_secret };
		_saveCache(c);
		return c.__fb_config;
	} catch (_) {
		const c = _loadCache();
		return c.__fb_config || c.__config || { app_id: '', has_secret: false };
	}
};

/**
 * Read app_id from localStorage cache (sync, for render).
 */
const getAppId = () => {
	const c = _loadCache();
	return c.__fb_config?.app_id || c.__config?.app_id || '';
};

/**
 * Save app_id (and optionally app_secret) to server + cache.
 */
const saveConfig = async ({ app_id, app_secret }) => {
	const body = {};
	if (app_id     !== undefined) body.app_id     = app_id;
	if (app_secret !== undefined) body.app_secret = app_secret;
	const data = await _serverPut('/fb/config', body);
	if (data.ok) {
		const c = _loadCache();
		c.__fb_config = {
			...(c.__fb_config || c.__config || {}),
			app_id:     app_id ?? c.__fb_config?.app_id ?? '',
			has_secret: app_secret ? true : !!(c.__fb_config?.has_secret),
		};
		_saveCache(c);
	}
	return data;
};

// ── Accounts ──────────────────────────────────────────────────────────────────

/**
 * List accounts from server (no tokens).
 * Updates localStorage cache.
 */
const listAccounts = async () => {
	try {
		const accounts = await _serverGet('/fb/accounts');
		if (Array.isArray(accounts)) {
			const c = _loadCache();
			c.__accounts = accounts;
			_saveCache(c);
			return accounts;
		}
		return [];
	} catch (_) {
		const c = _loadCache();
		return c.__accounts || [];
	}
};

/**
 * List accounts from localStorage cache (sync, for render).
 */
const listAccountsSync = () => {
	const c = _loadCache();
	return c.__accounts || [];
};

/**
 * Get full account including decrypted token (from server).
 */
const getAccountToken = async (fbUserId) => {
	return _serverGet(`/fb/accounts/${fbUserId}/token`);
};

/**
 * Delete an account from server + cache.
 */
const removeAccount = async (fbUserId) => {
	try {
		await _serverDelete(`/fb/accounts/${fbUserId}`);
	} catch (_) { /* server might be down */ }
	const c = _loadCache();
	if (c.__accounts) {
		c.__accounts = c.__accounts.filter((a) => a.fb_user_id !== fbUserId);
		_saveCache(c);
	}
};

/**
 * Check if a cached account's token is expired or close to expiry (within 5 min).
 */
const isTokenExpired = (account) => {
	const expiry = account?.expires_at || 0;
	if (!expiry) return false;
	return Date.now() > expiry - 5 * 60 * 1000;
};

/**
 * Format expires_at as a human-readable string.
 */
const formatExpiry = (expiresAt) => {
	if (!expiresAt) return 'Sin expiración';
	const d = new Date(expiresAt);
	return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
};

// ── OAuth2 Popup ──────────────────────────────────────────────────────────────

/**
 * Open Facebook OAuth2 popup.
 *
 * Tries Auth Code flow first (response_type=code) if App Secret is configured.
 * Falls back to Implicit flow (response_type=token) if not.
 *
 * @param {string} appId
 * @param {boolean} hasSecret — whether App Secret is saved in server
 * @returns {Promise<{ flow: 'code'|'token', code?: string, redirect_uri?: string, access_token?: string, expires_in?: number }>}
 */
const authorizeWithPopup = (appId, hasSecret = false) => {
	const id = appId || getAppId();
	if (!id) {
		return Promise.reject(new Error('No Facebook App ID configured. Go to Settings → Integrations.'));
	}

	return new Promise((resolve, reject) => {
		// Usar FB_OAUTH_CALLBACK_URL si está definida (permite usar .htm o cualquier dominio)
		// De lo contrario construir desde window.location.origin
		const _rtCfg = (typeof window !== 'undefined' && window.__RESTREAMER_CONFIG__) || {};
		const redirectUri = _rtCfg.FB_OAUTH_CALLBACK_URL
			? _rtCfg.FB_OAUTH_CALLBACK_URL
			: window.location.origin + '/oauth/facebook/callback.html';

		const scope = 'publish_video,pages_manage_posts,pages_read_engagement,pages_show_list,public_profile';

		// Prefer Auth Code flow when App Secret is available (gives 60-day tokens)
		const responseType = hasSecret ? 'code' : 'token';

		const authUrl =
			'https://www.facebook.com/v19.0/dialog/oauth' +
			`?client_id=${encodeURIComponent(id)}` +
			`&redirect_uri=${encodeURIComponent(redirectUri)}` +
			`&scope=${encodeURIComponent(scope)}` +
			`&response_type=${responseType}` +
			'&auth_type=rerequest';

		const popup = window.open(authUrl, 'fb_oauth', 'width=650,height=620,scrollbars=yes,resizable=yes');
		if (!popup) {
			return reject(new Error('Popup blocked. Allow popups for this site.'));
		}

		let resolved = false;

		const onMessage = (event) => {
			// Accept both old 'fb_oauth_token' type and new 'fb_oauth_result' type
			if (event.data && (event.data.type === 'fb_oauth_result' || event.data.type === 'fb_oauth_token')) {
				resolved = true;
				window.removeEventListener('message', onMessage);
				clearInterval(pollTimer);
					if (event.data.error) {
					reject(new Error(event.data.error));
				} else {
					resolve({
						flow:                        event.data.flow || 'token',
						code:                        event.data.code,
						redirect_uri:                event.data.redirect_uri,
						access_token:                event.data.access_token,
						expires_in:                  event.data.expires_in,
						long_lived_token:            event.data.long_lived_token || null,
						data_access_expiration_time: event.data.data_access_expiration_time || null,
					});
				}
			}
		};

		window.addEventListener('message', onMessage);

		const pollTimer = setInterval(() => {
			if (popup.closed && !resolved) {
				clearInterval(pollTimer);
				window.removeEventListener('message', onMessage);
				reject(new Error('Authorization cancelled.'));
			}
		}, 500);
	});
};

// ── Full connect flow ─────────────────────────────────────────────────────────

/**
 * Complete OAuth2 flow:
 *   1. Open popup → get code OR short-lived token
 *   2a. If code  → POST /fb/exchange (server does short→long exchange, saves to config.json)
 *   2b. If token → POST /fb/upgrade  (server upgrades short→long, saves to config.json)
 *         If server unavailable      → save short-lived token in localStorage only (2h fallback)
 *   3. Refresh list cache
 *
 * @param {string} appId
 * @param {boolean} hasSecret
 * @param {{ restreamer_channel_id?: string, restreamer_publication_id?: string }} [context]
 * @returns {Promise<{ account: object, tokenType: 'long'|'short', expiresAt: number }>}
 */
const connectAccount = async (appId, hasSecret, context = {}) => {
	const result = await authorizeWithPopup(appId, hasSecret);

	if (result.flow === 'code' && result.code) {
		// ── Auth Code flow → server does everything ──────────────────────────
		const data = await _serverPost('/fb/exchange', {
			code:                      result.code,
			redirect_uri:              result.redirect_uri,
			restreamer_channel_id:     context.restreamer_channel_id     || '',
			restreamer_publication_id: context.restreamer_publication_id || '',
		});
		if (data.error) throw new Error(data.error);
		// refresh cache
		await listAccounts();
		return { account: data.account, tokenType: 'long', expiresAt: data.account.expires_at };
	}

	if (result.flow === 'token' && result.access_token) {
		// ── Implicit flow ────────────────────────────────────────────────────
		// Priority 1: Facebook returned long_lived_token directly in the hash
		// (newer FB API behavior) → save to server without needing App Secret.
		if (result.long_lived_token) {
			try {
				const data = await _serverPost('/fb/save-token', {
					access_token:                result.access_token,
					long_lived_token:            result.long_lived_token,
					data_access_expiration_time: result.data_access_expiration_time || null,
					restreamer_channel_id:       context.restreamer_channel_id     || '',
					restreamer_publication_id:   context.restreamer_publication_id || '',
				});
				if (data.ok) {
					await listAccounts();
					return { account: data.account, tokenType: 'long', expiresAt: data.account.expires_at };
				}
			} catch (_) { /* fall through to upgrade */ }
		}

		// Priority 2: Try server-side upgrade (requires App Secret)
		try {
			const data = await _serverPost('/fb/upgrade', {
				access_token:              result.access_token,
				restreamer_channel_id:     context.restreamer_channel_id     || '',
				restreamer_publication_id: context.restreamer_publication_id || '',
			});
			if (data.ok) {
				await listAccounts();
				return { account: data.account, tokenType: 'long', expiresAt: data.account.expires_at };
			}
		} catch (_) { /* no App Secret configured — fall through */ }

		// Priority 3: Save directly to server without exchange (short-lived, persisted)
		try {
			const data = await _serverPost('/fb/save-token', {
				access_token:              result.access_token,
				restreamer_channel_id:     context.restreamer_channel_id     || '',
				restreamer_publication_id: context.restreamer_publication_id || '',
			});
			if (data.ok) {
				await listAccounts();
				return { account: data.account, tokenType: 'short', expiresAt: data.account.expires_at };
			}
		} catch (_) { /* server unavailable — last resort localStorage */ }

		// Last resort: localStorage only (no server persistence)
		const userInfo = await fetchUserInfo(result.access_token);
		const pages    = await fetchUserPages(result.access_token).catch(() => []);
		const shortAcc = {
			fb_user_id:                userInfo.id,
			name:                      userInfo.name,
			token_type:                'USER',
			access_token:              result.access_token,
			expires_at:                result.expires_in ? Date.now() + result.expires_in * 1000 : 0,
			scope_granted:             [],
			pages,
			restreamer_channel_id:     context.restreamer_channel_id     || '',
			restreamer_publication_id: context.restreamer_publication_id || '',
			updated_at:                Date.now(),
		};
		const lsCache = _loadCache();
		if (!lsCache.__accounts) lsCache.__accounts = [];
		const idx = lsCache.__accounts.findIndex((a) => a.fb_user_id === userInfo.id);
		const pub = { ...shortAcc }; delete pub.access_token;
		if (idx >= 0) lsCache.__accounts[idx] = pub; else lsCache.__accounts.push(pub);
		lsCache[userInfo.id] = shortAcc;
		_saveCache(lsCache);
		return { account: shortAcc, tokenType: 'short', expiresAt: shortAcc.expires_at };
	}

	throw new Error('No authorization data received from Facebook.');
};

// ── Refresh (renew long-lived token) ─────────────────────────────────────────

/**
 * Refresh a stored account's token via server.
 */
const refreshAccount = async (fbUserId) => {
	const data = await _serverPost(`/fb/refresh/${fbUserId}`, {});
	if (data.error) throw new Error(data.error);
	await listAccounts();
	return data.account;
};

// ── Direct Graph API calls (for short-lived local tokens) ────────────────────

const fetchUserInfo = async (accessToken) => {
	const res  = await fetch(`https://graph.facebook.com/v19.0/me?fields=id,name&access_token=${encodeURIComponent(accessToken)}`);
	const data = await res.json();
	if (data.error) throw new Error(data.error.message);
	return data;
};

const fetchUserPages = async (accessToken) => {
	const res  = await fetch(`https://graph.facebook.com/v19.0/me/accounts?fields=id,name,access_token,category&access_token=${encodeURIComponent(accessToken)}`);
	const data = await res.json();
	if (data.error) throw new Error(data.error.message);
	return (data.data || []).map((p) => ({ ...p, token_type: 'PAGE' }));
};

/**
 * Get usable access token for a given account entry (from server if possible).
 * entry: { fb_user_id, pageId? }
 */
const getTokenForEntry = async (fbUserId, pageId) => {
	try {
		const full = await getAccountToken(fbUserId);
		if (pageId) {
			const page = (full.pages || []).find((p) => p.id === pageId);
			return page ? page.access_token : null;
		}
		return full.access_token;
	} catch (_) {
		// fallback to localStorage short-lived token
		const c = _loadCache();
		const acc = c[fbUserId];
		if (!acc) return null;
		if (pageId) {
			const page = (acc.pages || []).find((p) => p.id === pageId);
			return page ? page.access_token : null;
		}
		return acc.access_token || null;
	}
};

// ── Legacy compatibility ──────────────────────────────────────────────────────
const setAppId = (app_id) => {
	const c = _loadCache();
	c.__fb_config = { ...(c.__fb_config || c.__config || {}), app_id };
	_saveCache(c);
};

export default {
	// Config
	getConfig,
	getAppId,
	setAppId,
	saveConfig,
	// Accounts
	listAccounts,
	listAccountsSync,
	getAccountToken,
	removeAccount,
	isTokenExpired,
	formatExpiry,
	// OAuth flows
	authorizeWithPopup,
	connectAccount,
	refreshAccount,
	// Graph API (direct)
	fetchUserInfo,
	fetchUserPages,
	getTokenForEntry,
};
